import { CarPerf, CarState, Input, quantise } from './car';
import { clamp, dcos, dsin } from './fmath';
import type { Track } from './track';
import { BRAKE_DECEL } from './tuning';

/**
 * A simple deterministic driver: pure pursuit along the centre line, with
 * braking for the next corner. Used for the lap-time tuning script and the
 * determinism tests now, and the seed of the AI drivers in M1 (design.md B7).
 * Arithmetic only — no trig beyond the sim's own dsin/dcos.
 */
export interface AutopilotOptions {
  /** 0.9 … 1.0: fraction of the corner speed it dares to carry. */
  pace: number;
  /** Lateral offset from the centre line, px (+ = right of travel). */
  lane: number;
}

export const DEFAULT_PILOT: AutopilotOptions = { pace: 1, lane: 0 };

function pointAt(track: Track, s: number): { x: number; y: number; seg: number } {
  const L = track.length;
  let d = s % L;
  if (d < 0) d += L;
  for (let i = 0; i < track.segs.length; i++) {
    const sg = track.segs[i];
    if (d <= sg.s0 + sg.len) {
      const t = (d - sg.s0) / sg.len;
      return { x: sg.ax + sg.dx * t, y: sg.ay + sg.dy * t, seg: i };
    }
  }
  const sg = track.segs[0];
  return { x: sg.ax, y: sg.ay, seg: 0 };
}

/** Speed (px/s) the pilot will take a corner whose direction changes by `dot` (cos of angle). */
function cornerSpeed(dot: number, perf: CarPerf, pace: number): number {
  // 90° street corner ≈ 150 px/s, flat-out when straight.
  const sharp = (1 - dot) / 2; // 0 straight … 0.5 at 90° … 1 hairpin
  const v = 330 - 360 * sharp;
  return clamp(v * pace, 70, perf.vmax);
}

export function autopilot(car: CarState, perf: CarPerf, track: Track, opt: AutopilotOptions = DEFAULT_PILOT): Input {
  const n = track.nearest(car.x, car.y);
  const sg = track.segs[n.seg];
  const s = sg.s0 + sg.len * n.t;
  const speed = Math.sqrt(car.vx * car.vx + car.vy * car.vy);

  // Steering: aim at a point ahead on the (offset) centre line.
  const look = 34 + speed * 0.22;
  const tp = pointAt(track, s + look);
  const tsg = track.segs[tp.seg];
  const tx = tp.x - tsg.uy * opt.lane;
  const ty = tp.y + tsg.ux * opt.lane;
  const dx = tx - car.x;
  const dy = ty - car.y;
  const dl = Math.sqrt(dx * dx + dy * dy) || 1;
  const fx = dcos(car.h);
  const fy = dsin(car.h);
  const crossv = (fx * dy - fy * dx) / dl; // + = target is to the right
  const dotv = (fx * dx + fy * dy) / dl;
  let steer = dotv < 0 ? (crossv >= 0 ? 1 : -1) : clamp(crossv * 3.2, -1, 1);

  // Speed: look for the next corner within braking range.
  let throttle = 1;
  let brake = 0;
  let distToCorner = sg.len * (1 - n.t);
  for (let k = 0; k < 4; k++) {
    const a = track.segs[(n.seg + k) % track.segs.length];
    const b = track.segs[(n.seg + k + 1) % track.segs.length];
    const dot = a.ux * b.ux + a.uy * b.uy;
    const vc = cornerSpeed(dot, perf, opt.pace);
    const need = speed * speed - vc * vc;
    if (need > 0 && need > 2 * BRAKE_DECEL * 0.85 * Math.max(0, distToCorner - 10)) {
      throttle = 0;
      brake = 1;
      break;
    }
    distToCorner += b.len;
    if (distToCorner > 900) break;
  }
  if (dotv < 0.3 && speed > 120) {
    throttle = 0;
  }
  if (Math.abs(steer) > 0.98 && speed < 30) steer = steer * 0.6;
  return quantise(steer, throttle, brake);
}
